import { defineStore } from 'pinia'
import { debounce } from 'lodash'
import { useGameStore } from '@/stores/gameStore'

/**
 * Gets a unique storage key based on player ID and game ID
 * Uses authPlayerId and authGameId which are available immediately after auth
 */
function getStorageKey(): string | null {
  const gameStore = useGameStore()
  const playerId = gameStore.authPlayerId
  const gameId = gameStore.authGameId
  if (!playerId || !gameId) return null

  return `mapCamera_${playerId}_${gameId}`
}

export const useMapCameraStore = defineStore('mapCameraStore', {
  state: () => ({
    savedCameraX: 0 as number,
    savedCameraY: 0 as number,
    savedZoomFactor: 1 as number,
    hasSavedPosition: false as boolean
  }),

  getters: {
    canRestore(): boolean {
      return this.hasSavedPosition
    }
  },

  actions: {
    /**
     * Load camera state from localStorage for current player/arena
     */
    loadFromStorage() {
      const key = getStorageKey()
      if (!key) return

      const stored = localStorage.getItem(key)
      if (stored) {
        try {
          const data = JSON.parse(stored)
          this.savedCameraX = data.savedCameraX ?? 0
          this.savedCameraY = data.savedCameraY ?? 0
          this.savedZoomFactor = data.savedZoomFactor ?? 1
          this.hasSavedPosition = data.hasSavedPosition ?? false
        } catch {
          // Invalid stored data, ignore
        }
      }
    },

    /**
     * Save camera state to localStorage for current player/arena
     */
    saveToStorage() {
      const key = getStorageKey()
      if (!key) return

      localStorage.setItem(
        key,
        JSON.stringify({
          savedCameraX: this.savedCameraX,
          savedCameraY: this.savedCameraY,
          savedZoomFactor: this.savedZoomFactor,
          hasSavedPosition: this.hasSavedPosition
        })
      )
    },

    _debouncedSave: debounce(function (this: any, x: number, y: number, zoom: number) {
      this.savedCameraX = x
      this.savedCameraY = y
      this.savedZoomFactor = zoom
      this.hasSavedPosition = true
      this.saveToStorage()
    }, 500),

    saveCameraState(x: number, y: number, zoom: number) {
      ;(this._debouncedSave as any).call(this, x, y, zoom)
    },

    clearSavedPosition() {
      this.savedCameraX = 0
      this.savedCameraY = 0
      this.savedZoomFactor = 1
      this.hasSavedPosition = false
      this.saveToStorage()
    }
  }
})
